import React from 'react';

import { Box, ButtonBase, makeStyles } from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    border: '1px solid rgba(19, 95, 130, 0.14)',
    background:
      'linear-gradient(180deg, rgba(250, 253, 255, 0.94) 0%, rgba(241, 248, 252, 0.92) 100%)',
    boxShadow: '0 8px 16px rgba(16, 73, 105, 0.038), 0 1px 0 rgba(209, 227, 238, 0.54)',
    overflow: 'hidden',
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 14px 26px rgba(16, 73, 105, 0.07), 0 2px 0 rgba(198, 220, 235, 0.78)',
    },
  },
  cardExpanded: {
    borderColor: 'rgba(37, 134, 188, 0.28)',
    boxShadow: '0 16px 30px rgba(16, 73, 105, 0.085), 0 2px 0 rgba(188, 215, 232, 0.82)',
  },
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.25, 2.5),
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&:hover $iconWrap': {
      boxShadow: '0 12px 22px rgba(16, 73, 105, 0.11), 0 1px 0 rgba(198, 220, 235, 0.72)',
      background: 'linear-gradient(180deg, rgba(248, 252, 255, 0.98) 0%, rgba(227, 240, 248, 0.98) 100%)',
      borderColor: 'rgba(37, 134, 188, 0.22)',
    },
  },
  triggerContent: {
    minWidth: 0,
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  index: {
    width: 42,
    height: 42,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.4,
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(37, 134, 188, 0.1)',
  },
  textWrap: {
    minWidth: 0,
  },
  title: {
    marginBottom: theme.spacing(0.5),
    fontSize: 18,
    lineHeight: 1.35,
    fontWeight: 700,
    color: '#124a68',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
  },
  summary: {
    fontSize: 14,
    lineHeight: 1.65,
    color: 'rgba(18, 74, 104, 0.78)',
  },
  meta: {
    marginTop: theme.spacing(1.25),
    display: 'inline-flex',
    padding: theme.spacing(0.6, 1.2),
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    color: '#124a68',
    backgroundColor: 'rgba(255,255,255,0.84)',
    border: '1px solid rgba(37, 134, 188, 0.14)',
  },
  iconWrap: {
    width: 40,
    height: 40,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: theme.palette.primary.main,
    background: 'linear-gradient(180deg, rgba(246, 251, 255, 0.96) 0%, rgba(231, 242, 249, 0.96) 100%)',
    border: '1px solid rgba(37, 134, 188, 0.14)',
    boxShadow: '0 8px 16px rgba(16, 73, 105, 0.08), 0 1px 0 rgba(205, 225, 238, 0.7)',
    transition: 'background 180ms ease, box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
  },
  icon: {
    transition: 'transform 180ms ease',
  },
  iconOpen: {
    transform: 'rotate(180deg)',
  },
  content: {
    padding: theme.spacing(0, 2.5, 2.5),
    borderTop: '1px solid rgba(19, 95, 130, 0.08)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2, 2),
    },
  },
}));

function Question({ id, title, summary, meta, indexLabel, isExpanded, toggle, enabled, children }) {
  const classes = useStyles();

  if (!enabled) {
    return <></>;
  }

  return (
    <Box
      id={`help-question-${id}`}
      className={`${classes.card} ${isExpanded ? classes.cardExpanded : ''}`.trim()}
    >
      <ButtonBase
        focusRipple
        className={classes.trigger}
        onClick={() => toggle(id)}
        aria-expanded={isExpanded}
        aria-label={title}
      >
        <Box className={classes.triggerContent}>
          <Box className={classes.index}>{indexLabel}</Box>
          <Box className={classes.textWrap}>
            <Box className={classes.title}>{title}</Box>
            {summary && <Box className={classes.summary}>{summary}</Box>}
          </Box>
        </Box>

        <Box className={classes.iconWrap}>
          <ExpandMoreIcon className={`${classes.icon} ${isExpanded ? classes.iconOpen : ''}`.trim()} />
        </Box>
      </ButtonBase>

      {isExpanded && <Box className={classes.content}>{children}</Box>}
    </Box>
  );
}

export default Question;
